import { minute } from "@pentacode/core/src/time";
import { version, VersionInfo } from "@pentacode/core/src/version";
import { Workbox } from "workbox-window";
import { getSingleton } from "../lib/singleton";
import type { UpdateNotification } from "../elements/update-notification";
import "../elements/update-notification";
import { LitElement } from "lit";

type Constructor<T> = new (...args: any[]) => T;

/**
 * This class is responsible for registering the service worker (sw.ts) and handling updates
 * to both the service worker itself, as well as the application.
 *
 * The app version and service worker version can be different because as soon as a new
 * service worker is available it is installed, but the app is only update on a page reload,
 * which can be triggered by the update dialog, but also be dismissed.
 *
 * This class repeatedly checks for service worker updates, and once it installed a new service worker version,
 * checks for the new app.
 *
 * After that, it handles 'outdated-client' events and specifically checks for app updates.
 */
export function ServiceWorker<B extends Constructor<LitElement>>(baseClass: B) {
    return class extends baseClass {
        private _wb?: Workbox;

        constructor(...args: any[]) {
            super(...args);
            void this._initSW();
        }

        private async _initSW() {
            if (!("serviceWorker" in navigator)) {
                return;
            }

            this._wb = new Workbox("/sw.js");

            // "waiting" means there is a new service worker waiting that is not installed yet
            this._wb.addEventListener("waiting", async (e) => {
                if (e.sw) {
                    await this._onServiceWorkerWaiting();
                }
            });

            /* Outdated client can be fired per request, so we only listen
             * once, check after receiving the event, and register again */
            const listenForOutDatedClientOnce = () =>
                this.addEventListener(
                    "outdated-client",
                    async () => {
                        console.warn("Outdated client detected, checking for update.");
                        await this._checkForAppUpdate();
                        listenForOutDatedClientOnce();
                    },
                    { once: true }
                );
            listenForOutDatedClientOnce();

            await this._wb.register();

            this._pollForUpdates();
        }

        private async _onServiceWorkerWaiting() {
            if (!this._wb) {
                return;
            }

            this._wb.addEventListener("controlling", async () => {
                await this._onServiceWorkerControlling();
            });

            //tell the service worker to skip waiting, i.e. install immediately
            await this._wb.messageSW({ type: "INSTALL_UPDATE" });
        }

        private async _checkForServiceWorkerUpdates() {
            if (!this._wb) {
                return;
            }

            try {
                await this._wb.update();
            } catch (e) {
                console.error("Error checking for updates", e);
            }
        }

        private async _onServiceWorkerControlling() {
            if (!this._wb) {
                return;
            }

            const info = (await this._wb.messageSW({
                type: "GET_VERSION",
                currentVersion: version,
            })) as VersionInfo;

            /**
             * We always update when the service worker changed. This is normally the case when we
             * release an update, because then the version in the version info is different.
             */
            void (getSingleton("ptc-update-notification") as UpdateNotification).show(info);
        }

        /** This checks if there is an app update available */
        private async _checkForAppUpdate() {
            if (!this._wb) {
                return;
            }

            const info = (await this._wb.messageSW({
                type: "GET_VERSION",
                currentVersion: version,
            })) as VersionInfo;

            /** Specifically for outdated clients, we check if there is an app-update available.
             * In the unlikely case that we get an outdated client event, but we do not actually
             * have a new service worker, we don't want to show the update dialog.
             */
            if (info.version !== version) {
                void (getSingleton("ptc-update-notification") as UpdateNotification).show(info);
            }
        }

        private _pollForUpdates(interval = 10 * minute) {
            void this._checkForServiceWorkerUpdates();
            setInterval(() => {
                void this._checkForServiceWorkerUpdates();
            }, interval);
        }
    };
}
